import { useTranslation } from "react-i18next";
import { Ban, Loader2, Wallpaper } from "lucide-react";

import { useCenter } from "../../../center";
import { imageChoice } from "../../domain/meetingBackground";
import type { BackgroundEffectChannel } from "../../application/useBackgroundEffect";
import MeetingBackgroundShelf from "./MeetingBackgroundShelf";

/**
 * Choosing what is behind you.
 *
 * The control is hidden outright where the browser cannot segment video —
 * offering a background that will not work, in a product where the reason to
 * want one is not wanting to be seen in your own home, is worse than not
 * offering it at all.
 *
 * It sits under the camera rather than in the bar: what is behind you is
 * decided once and then forgotten, unlike the microphone, which is reached for
 * constantly.
 *
 * The order is the order of commitment. "None" and "Blur" first, because they
 * are what a patient on a borrowed laptop needs and neither asks anything of
 * them; then the therapist's own pictures; then the centre's, in a group of
 * their own so that nobody deletes a colleague's wall thinking it was theirs.
 *
 * The state deliberately does not live here. This renders inside a menu, and a
 * menu unmounts when it closes — so the effect that removes the background
 * never got to run, and turning it off did nothing at all. The channel is held
 * by the bar, which is on screen for the whole call.
 *
 * The state is in the word as well as the fill: "is my room hidden" is not a
 * question anyone should have to answer by comparing two greys.
 */
export default function MeetingBackground({
    channel,
}: {
    channel: BackgroundEffectChannel;
}) {
    const { supported, choice, blurred, imageId, pending, failed, choose, setBlurred } =
        channel;
    const { t } = useTranslation();
    // Only an administrator fills the centre's shelf; every member uses it.
    const { isAdmin } = useCenter();

    if (!supported) return null;

    return (
        <div className="flex flex-col gap-1.5">
            <button
                type="button"
                onClick={() => choose("none")}
                aria-pressed={choice === "none"}
                disabled={pending}
                data-testid="background-none"
                className={`room-menu-item ${
                    choice === "none" ? "bg-room-active" : ""
                } disabled:opacity-50`}
            >
                <Ban size={16} aria-hidden="true" />
                <span>{t("meetingBackground.none")}</span>
            </button>

            <button
                type="button"
                onClick={() => setBlurred(!blurred)}
                aria-pressed={blurred}
                disabled={pending}
                data-testid="background-effect"
                className={`room-menu-item ${blurred ? "bg-room-active" : ""} disabled:opacity-50`}
            >
                {pending ? (
                    <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                ) : (
                    <Wallpaper size={16} aria-hidden="true" />
                )}
                <span>
                    {t(blurred ? "meetingBackground.blurred" : "meetingBackground.blur")}
                </span>
            </button>

            <hr className="my-1 border-room-line-soft" />

            <MeetingBackgroundShelf
                scope="own"
                variant="room"
                selectedId={imageId}
                onSelect={(background) => choose(imageChoice(background.id))}
            />

            <hr className="my-1 border-room-line-soft" />

            <MeetingBackgroundShelf
                scope="center"
                variant="room"
                canManage={isAdmin}
                selectedId={imageId}
                onSelect={(background) => choose(imageChoice(background.id))}
            />

            {failed && (
                <p
                    role="alert"
                    className="px-2 pb-1 text-xs font-medium text-room-live"
                    data-testid="background-effect-error"
                >
                    {t("meetingBackground.failed")}
                </p>
            )}
        </div>
    );
}
